// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.

/**
 * The recording-analysis artefact: a pitch-over-time card (PNG), a CSV of the
 * trace, and a one-line summary. Drawing + serialising; no analysis here.
 */

import type { AnalyseResult } from './analysis';
import { noteLabel } from './notes';
import type { Accidental } from './types';
import { formatCents, formatClock, formatHz } from './format';

export interface ReportMeta {
  source: string;
  referenceA: number;
  accidental: Accidental;
  when: string;
}

const W = 1200;
const H = 675;
const CENTS_RANGE = 50;

export function renderReportCard(canvas: HTMLCanvasElement, result: AnalyseResult, meta: ReportMeta): void {
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  // Background.
  ctx.fillStyle = '#0a0910';
  ctx.fillRect(0, 0, W, H);
  const grad = ctx.createLinearGradient(0, 0, W, H);
  grad.addColorStop(0, 'rgba(124,92,255,0.10)');
  grad.addColorStop(1, 'rgba(55,214,122,0.05)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  const pad = 64;
  const plotTop = 210;
  const plotBottom = H - 96;
  const plotLeft = pad + 44;
  const plotRight = W - pad;
  const plotH = plotBottom - plotTop;
  const midY = plotTop + plotH / 2;

  // Header.
  ctx.fillStyle = '#efeaff';
  ctx.font = '600 30px -apple-system, system-ui, sans-serif';
  ctx.textBaseline = 'alphabetic';
  ctx.textAlign = 'left';
  ctx.fillText('Tunewell · tuning report', pad, 72);

  const dom = result.dominant;
  ctx.font = '700 72px -apple-system, system-ui, sans-serif';
  ctx.fillStyle = '#ffffff';
  const noteText = dom ? noteLabel(dom.midi, meta.accidental) : 'No clear pitch';
  ctx.fillText(noteText, pad, 150);

  if (dom) {
    const median = result.medianCents;
    ctx.font = '500 30px -apple-system, system-ui, sans-serif';
    ctx.fillStyle = median !== null && Math.abs(median) <= 5 ? '#37d67a' : '#ffb020';
    const centsText = median !== null ? `${formatCents(median)} cents (median)` : '';
    const domHz = meta.referenceA * Math.pow(2, (dom.midi - 69) / 12);
    ctx.fillText(`${formatHz(domHz)} Hz target · ${centsText}`, pad + 260, 150);
  }

  // Y grid (cents).
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.fillStyle = 'rgba(255,255,255,0.42)';
  ctx.font = '400 20px -apple-system, system-ui, sans-serif';
  ctx.textAlign = 'right';
  for (let c = -CENTS_RANGE; c <= CENTS_RANGE; c += 25) {
    const y = midY - (c / CENTS_RANGE) * (plotH / 2);
    ctx.strokeStyle = c === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.07)';
    ctx.beginPath();
    ctx.moveTo(plotLeft, y);
    ctx.lineTo(plotRight, y);
    ctx.stroke();
    ctx.fillText(c === 0 ? '0' : c > 0 ? `+${c}` : `−${Math.abs(c)}`, plotLeft - 12, y + 6);
  }

  // In-tune band.
  const bandTop = midY - (5 / CENTS_RANGE) * (plotH / 2);
  const bandBottom = midY + (5 / CENTS_RANGE) * (plotH / 2);
  ctx.fillStyle = 'rgba(55,214,122,0.12)';
  ctx.fillRect(plotLeft, bandTop, plotRight - plotLeft, bandBottom - bandTop);

  // Trace.
  const dur = Math.max(0.001, result.duration);
  const xFor = (t: number): number => plotLeft + (t / dur) * (plotRight - plotLeft);
  const yFor = (cents: number): number => midY - (Math.max(-CENTS_RANGE, Math.min(CENTS_RANGE, cents)) / CENTS_RANGE) * (plotH / 2);

  ctx.fillStyle = '#7c5cff';
  for (const s of result.trace) {
    if (s.cents === null) continue;
    const x = xFor(s.t);
    const y = yFor(s.cents);
    const good = Math.abs(s.cents) <= 5;
    ctx.fillStyle = good ? '#37d67a' : Math.abs(s.cents) <= 20 ? '#c7b8ff' : '#ffb020';
    ctx.globalAlpha = 0.35 + Math.min(0.6, s.clarity) * 0.65;
    ctx.beginPath();
    ctx.arc(x, y, 2.6, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Axis labels.
  ctx.fillStyle = 'rgba(255,255,255,0.42)';
  ctx.font = '400 20px -apple-system, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText('cents sharp / flat over time', plotLeft, plotTop - 18);
  ctx.textAlign = 'right';
  ctx.fillText(`${formatClock(result.duration)}`, plotRight, plotBottom + 30);
  ctx.textAlign = 'left';
  ctx.fillText('0:00', plotLeft, plotBottom + 30);

  // Footer.
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = '400 20px -apple-system, system-ui, sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(`A4 = ${result.dominant ? meta.referenceA : meta.referenceA} Hz · ${meta.source}`, pad, H - 34);
  ctx.textAlign = 'right';
  ctx.fillText('tunewell.benrichardson.dev', W - pad, H - 34);
}

export function summaryText(result: AnalyseResult, meta: ReportMeta): string {
  if (!result.dominant) {
    return `Tunewell found no clear pitch in ${meta.source}. Try a cleaner recording of a single sustained note.`;
  }
  const label = noteLabel(result.dominant.midi, meta.accidental);
  const median = result.medianCents;
  const centsPart = median !== null ? `${formatCents(median)} cents (median)` : 'unknown deviation';
  return `Tunewell · ${meta.source}: dominant note ${label}, ${centsPart} at A4 = ${meta.referenceA} Hz, ${formatClock(result.duration)}.`;
}

export function toCsv(result: AnalyseResult, meta: ReportMeta): string {
  const lines: string[] = [];
  lines.push(`# Tunewell tuning trace`);
  lines.push(`# source,${csvEscape(meta.source)}`);
  lines.push(`# reference_a_hz,${meta.referenceA}`);
  lines.push(`# duration_s,${result.duration.toFixed(3)}`);
  lines.push(`# dominant_note,${result.dominant ? noteLabel(result.dominant.midi, meta.accidental) : ''}`);
  lines.push(`# median_cents,${result.medianCents !== null ? result.medianCents.toFixed(2) : ''}`);
  lines.push('time_s,frequency_hz,cents,clarity');
  for (const s of result.trace) {
    lines.push(
      [s.t.toFixed(3), s.hz !== null ? s.hz.toFixed(3) : '', s.cents !== null ? s.cents.toFixed(2) : '', s.clarity.toFixed(3)].join(','),
    );
  }
  return lines.join('\n');
}

function csvEscape(s: string): string {
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
